import { add } from 'date-fns';
import { LocaleSettings } from 'primeng-lts/calendar';

export interface GetWeekdayNameOptions {
  startDay?: number;
  format?: 'long' | 'short' | 'narrow';
  locale?: string | null;
  todayLabel?: string;
  clearLabel?: string;
}

const getWeekdayNameOptionsDefaults: GetWeekdayNameOptions = {
  startDay: 0,
  format: 'short',
  locale: navigator.language,
  todayLabel: 'Today',
  clearLabel: 'Clear',
};

const TE_ST_LOCALE = {
  firstDayOfWeek: 0,
  dayNames: [
    '*Sunday',
    '*Monday',
    '*Tuesday',
    '*Wednesday',
    '*Thursday',
    '*Friday',
    '*Saturday',
  ],
  dayNamesShort: ['*Sun', '*Mon', '*Tue', '*Wed', '*Thu', '*Fri', '*Sat'],
  dayNamesMin: ['*S', '*M', '*T', '*W', '*T', '*F', '*S'],
  monthNames: [
    '*January',
    '*February',
    '*March',
    '*April',
    '*May',
    '*June',
    '*July',
    '*August',
    '*September',
    '*October',
    '*November',
    '*December',
  ],
  monthNamesShort: [
    '*Jan',
    '*Feb',
    '*Mar',
    '*Apr',
    '*May',
    '*Jun',
    '*Jul',
    '*Aug',
    '*Sep',
    '*Oct',
    '*Nov',
    '*Dec',
  ],
};

export const range = (start: number, end: number): number[] => {
  return [...Array(end - start + 1).keys()].map((k) => k + start);
};

export const getWeekdayNames = (
  options: GetWeekdayNameOptions = {}
): string[] => {
  const referenceDate = new Date(1978, 11, 31); // This date is a Sunday
  const mergedOptions = { ...getWeekdayNameOptionsDefaults, ...options };

  return range(0, 6).map((day) => {
    return add(referenceDate, { days: day }).toLocaleDateString(
      mergedOptions.locale,
      { weekday: mergedOptions.format }
    );
  });
};

export const getMonthNames = (
  options: GetWeekdayNameOptions = {}
): string[] => {
  const referenceDate = new Date(1978, 0, 1); // This date is a January
  const mergedOptions = { ...getWeekdayNameOptionsDefaults, ...options };

  return range(0, 11).map((month) => {
    return add(referenceDate, { months: month }).toLocaleDateString(
      mergedOptions.locale,
      { month: mergedOptions.format }
    );
  });
};

const firstDayMap: { [key: string]: number } = {
  // North America
  'en-us': 0,
  'en-ca': 0,
  'fr-ca': 1,
  'es-mx': 0,

  // Europe (mostly Monday)
  'en-gb': 1,
  'fr-fr': 1,
  'de-de': 1,
  'es-es': 1,
  'it-it': 1,
  'pt-pt': 1,
  'nl-nl': 1,
  'da-dk': 1,
  'sv-se': 1,
  'fi-fi': 1,
  'no-no': 1,
  'pl-pl': 1,
  'cs-cz': 1,
  'sk-sk': 1,
  'hu-hu': 1,
  'ru-ru': 1,
  'uk-ua': 1,

  // Middle East & Islamic world (Saturday or Sunday)
  'ar-eg': 6, // Egypt - Saturday
  'ar-sa': 6, // Saudi Arabia - Saturday
  'he-il': 0, // Israel - Sunday

  // Asia
  'ja-jp': 1,
  'zh-cn': 1,
  'zh-tw': 0,
  'ko-kr': 1,
  'hi-in': 1,
  'th-th': 1,
  'vi-vn': 1,

  // Oceania
  'en-au': 1,
  'en-nz': 1,

  // Africa
  'en-za': 1,
  'sw-ke': 1,
  'yo-ng': 1,

  // South America (mostly Sunday)
  'pt-br': 0,
  'es-ar': 0,
  'es-cl': 0,
  'es-co': 0,
  'es-pe': 0,
};

export const getFirstDayOfWeek = (locale: string): number => {
  return firstDayMap[locale.toLowerCase()] ?? 0; // default to Sunday
};

export const buildLocale = (
  options: GetWeekdayNameOptions = {}
): LocaleSettings => {
  const mergedOptions = {
    ...getWeekdayNameOptionsDefaults,
    ...options,
  };
  switch (mergedOptions.locale) {
    case 'te-st':
      return {
        ...TE_ST_LOCALE,
        today: mergedOptions.todayLabel,
        clear: mergedOptions.clearLabel,
      };
    default:
      return {
        firstDayOfWeek: getFirstDayOfWeek(mergedOptions.locale),
        dayNames: getWeekdayNames({ ...mergedOptions, format: 'long' }),
        dayNamesShort: getWeekdayNames({
          ...mergedOptions,
          format: 'short',
        }),
        dayNamesMin: getWeekdayNames({
          ...mergedOptions,
          format: 'narrow',
        }),
        monthNames: getMonthNames({
          ...mergedOptions,
          format: 'long',
        }),
        monthNamesShort: getMonthNames({
          ...mergedOptions,
          format: 'short',
        }),
        today: mergedOptions.todayLabel,
        clear: mergedOptions.clearLabel,
      } as LocaleSettings;
  }
};
